import * as React from "react";
import { Block } from "kanopi-components/Block";
import { LabelXSmall } from "kanopi-components/typography";
import { Question, SIZE } from "../Question";
import { toCamelCase } from "kanopi-components/utils/string";
import { useDebounceCallback } from "@react-hook/debounce";
import { useForm } from "react-weaver";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useYupSchema } from "kanopi-components/useYupSchema";

type TQuestionSetProps = TODO;

export const CoverSectionQuestionSet: React.FC<TQuestionSetProps> = ({
  questionSet = {},
  onSubmit,
  onChange,
  onError,
  initialValues,
  disabled,
}) => {
  const questions = (questionSet?.questions || []).sort((a, b) => a.order - b.order);
  // This function is a little annoying; it transforms from ID keys to
  // storage path keys. It's only needed because Yup can't handle
  // periods in the field name.
  function handleSubmit(values) {
    const result = {};
    for (const [k, v] of Object.entries(values)) {
      const question = questions.find((q) => q.id == k);
      result[question.storagePath] = v;
    }
    onSubmit?.(result);
  }
  const validator = useYupSchema({
    fields: questions,
    formValidators: questionSet.validators,
  });
  const { fieldProps, submit, updateValues } = useForm({
    initialValues,
    validator,
    onSubmit: handleSubmit,
    onChange,
    onError,
    noPositive: true,
  });

  const [, theme] = useStyletron();

  const handleChange = useDebounceCallback(
    () => {
      submit();
    },
    500,
    false
  );

  React.useEffect(() => {
    if (!disabled) updateValues(initialValues);
  }, [initialValues, disabled]);

  return (
    <>
      {questions.map((q, i) => {
        const { label } = q;
        return (
          <Block
            key={q.id}
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            paddingTop="scale400"
            paddingBottom="scale400"
            overrides={{
              Block: {
                style: {
                  ":first-of-type": {
                    borderTop: "1px solid lightgrey",
                  },
                  borderBottom: "1px solid lightgrey",
                },
              },
            }}
          >
            {label && (
              <LabelXSmall
                color={theme.colors.contentTertiary}
                flex={1}
                overrides={{
                  Block: {
                    style: { textAlign: "right" },
                    props: {
                      "data-testid": `CSQS-${toCamelCase(label)}`,
                    },
                  },
                }}
              >
                {label?.toUpperCase()}
              </LabelXSmall>
            )}
            <Block flex={3} marginLeft={label ? "scale600" : 0} alignItems="center">
              <Question
                size={SIZE.mini}
                hideLabel
                question={q}
                autoFocus={!i}
                {...fieldProps[q.id]}
                onChange={(...args) => {
                  fieldProps[q.id].onChange(...args);
                  handleChange();
                }}
                disabled={fieldProps?.disabled?.disabled || disabled}
                editable={!!onSubmit}
              />
            </Block>
          </Block>
        );
      })}
    </>
  );
};
